import { getCurrency } from 'utils/utils';
import {
  getAirlines,
  getAirports,
  getBaggage,
  getFlightTypes,
  getStopsInbound,
  getTimes,
  getBounds,
} from 'utils/dynamic/dynamic-fl';
import { DynamicFlight } from '../interfaces/DynamicResponse';

export interface TimeFilter {
  min: number;
  max: number;
  value: [number, number];
}

export class FlightFilterModel {
  stops: {
    number: number;
    label: string;
    checked: boolean;
    minPrice: string;
    disabled?: boolean;
  }[];
  baggage: {
    number: number;
    label: string;
    checked: boolean;
    minPrice: string;
    disabled?: boolean;
  }[];
  price: {
    min: number;
    max: number;
    currencySign: string;
    value: [number, number];
  };
  departureOutboundTime?: TimeFilter;
  arrivalOutboundTime?: TimeFilter;
  departureInboundTime?: TimeFilter;
  arrivalInboundTime?: TimeFilter;
  duration: TimeFilter;
  stopDuration: TimeFilter;
  airlines: {
    code: string;
    label: string;
    checked: boolean;
    minPrice: string;
  }[];
  airports: {
    code: string;
    label: string;
    checked: boolean;
    minPrice: string;
  }[];
  flightTypes: {
    code: string;
    label: string;
    checked: boolean;
    minPrice: string;
  }[];

  constructor(flights: DynamicFlight[], currency: string) {
    const currencySign = getCurrency(currency) || '';
    this.stops = getStopsInbound(flights, currencySign);
    this.baggage = getBaggage(flights, currencySign);
    this.airlines = getAirlines(flights, currencySign);
    this.airports = getAirports(flights, currencySign);
    this.flightTypes = getFlightTypes(flights, currencySign);

    /// TIME ///
    const departureOutboundTimes = flights.map((flight) => {
      const departureTime = flight.outbound?.departureDateTime;
      return departureTime ? new Date(departureTime).getTime() : 0;
    });

    const arrivalOutboundTimes = flights.map((flight) => {
      const arrivalTime = flight.outbound?.arrivalDateTime;
      return arrivalTime ? new Date(arrivalTime).getTime() : 0;
    });

    this.departureOutboundTime = getTimes(departureOutboundTimes);
    this.arrivalOutboundTime = getTimes(arrivalOutboundTimes);
    const departureInboundTimes = flights.map((flight) => {
      const departureTime = flight.inbound?.departureDateTime;
      return departureTime ? new Date(departureTime).getTime() : 0;
    });

    const arrivalInboundTimes = flights.map((flight) => {
      const arrivalTime = flight.inbound?.arrivalDateTime;
      return arrivalTime ? new Date(arrivalTime).getTime() : 0;
    });
    this.departureInboundTime = getTimes(departureInboundTimes);
    this.arrivalInboundTime = getTimes(arrivalInboundTimes);

    /// PRICE ///
    const prices = flights.map((flight) => flight.priceDiff);
    const minPrice = Math.floor(prices.length ? Math.min(...prices) : 0);
    const maxPrice = Math.ceil(prices.length ? Math.max(...prices) : 1);
    this.price = {
      min: minPrice,
      max: maxPrice,
      currencySign,
      value: [minPrice, maxPrice],
    };

    /// DURATION ///
    const durations = flights
      .map((flight) => {
        const bounds = getBounds(flight);
        const durations = bounds.map((bound) => bound.elapsedDTime).filter((dur) => dur);
        return Math.min(...durations);
      })
      .filter((dur) => isFinite(dur));
    const minDuration = Math.floor(durations.length ? Math.min(...durations) : 0);
    const maxDuration = Math.ceil(durations.length ? Math.max(...durations) : 1);
    this.duration = {
      min: minDuration,
      max: maxDuration,
      value: [minDuration, maxDuration],
    };

    const stopDurations = flights.map((flight) => {
      const bounds = getBounds(flight);
      const stopDurations = bounds.map((bound) => {
        return bound.maxConnDTime;
      });

      const maxDur = Math.max(...stopDurations);
      return maxDur;
    });

    const minStopDuration = 0;
    const maxStopDuration = Math.ceil(stopDurations.length ? Math.max(...stopDurations) : 1);
    this.stopDuration = {
      min: minStopDuration,
      max: maxStopDuration,
      value: [minStopDuration, maxStopDuration],
    };
  }
}
